import React, { useCallback, useEffect, useState } from "react";
import { Box } from "@mui/material";
import BackendAxios from "../BackendAxios";
import Filters from "./components/Filters";
import Cards from "./components/Cards";
import DetailsDialog from "./components/DetailsDialog";
import "./styles.css";

const PAGE_SIZE = 8;

function hasAnyFieldFilter(config) {
  if (!config) return false;
  const values = Array.isArray(config.values)
    ? config.values.filter(Boolean)
    : [];
  return (
    values.length > 0 ||
    (config.min !== undefined && config.min !== "") ||
    (config.max !== undefined && config.max !== "")
  );
}

function normalizeFilterState(filters) {
  const normalized = {
    fields: {},
    squareFootage: {
      includeBasement: Boolean(filters?.squareFootage?.includeBasement),
    },
  };

  for (const [fieldId, config] of Object.entries(filters?.fields || {})) {
    const values = Array.isArray(config?.values)
      ? config.values.filter(Boolean)
      : [];
    const min = config?.min ?? "";
    const max = config?.max ?? "";

    if (values.length > 0 || min !== "" || max !== "") {
      normalized.fields[fieldId] = { values, min, max };
    }
  }

  for (const [fieldId, config] of Object.entries(filters?.features || {})) {
    const normalizedValues =
      config === true
        ? ["true"]
        : Array.isArray(config?.values)
        ? config.values.filter(Boolean)
        : Array.isArray(config)
        ? config.filter(Boolean)
        : typeof config === "string" && config.trim()
        ? [config.trim()]
        : [];

    if (normalizedValues.length === 0) {
      continue;
    }

    const existingFieldConfig = normalized.fields[fieldId] || {
      values: [],
      min: "",
      max: "",
    };

    normalized.fields[fieldId] = {
      ...existingFieldConfig,
      values: [...new Set([...existingFieldConfig.values, ...normalizedValues])],
    };
  }

  return normalized;
}

function buildFilterPayload(filters) {
  const normalized = normalizeFilterState(filters);
  const payload = {};

  if (Object.keys(normalized.fields).length > 0) {
    payload.fields = normalized.fields;
  }
  payload.squareFootage = {
    includeBasement: Boolean(normalized.squareFootage?.includeBasement),
  };

  return payload;
}

function parseOptionalNumber(value) {
  if (value === undefined || value === null) return null;
  if (typeof value === "string" && value.trim() === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function clampNumber(value, min, max) {
  return Math.min(max, Math.max(min, value));
}

function isNumericInput(value) {
  if (value === undefined || value === null) return false;
  if (typeof value === "string" && value.trim() === "") return false;
  return Number.isFinite(Number(value));
}

function getAvailableFiltersFromResponse(data) {
  if (data?.availableFilters) {
    return {
      fields: Array.isArray(data.availableFilters.fields)
        ? data.availableFilters.fields
        : [],
    };
  }

  return {
    fields: Array.isArray(data?.availableFields) ? data.availableFields : [],
  };
}

function BrowseDesigns(props) {
  const { sizing } = props;
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState(null);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState(null);
  const [filterDrawerOpen, setFilterDrawerOpen] = useState(false);
  const [filtersRefreshing, setFiltersRefreshing] = useState(false);
  const [activeNumberSliderFieldId, setActiveNumberSliderFieldId] =
    useState(null);
  const [filterState, setFilterState] = useState({
    fields: {},
    squareFootage: { includeBasement: false },
  });
  const [allFilterCatalog, setAllFilterCatalog] = useState({ fields: [] });
  const [availableFilters, setAvailableFilters] = useState({ fields: [] });

  const fetchEntries = async (
    requestedPage,
    nextFilterState = filterState,
    lockFilters = false,
    append = false
  ) => {
    if (append) {
      setLoadingMore(true);
    } else {
      setLoading(true);
    }
    if (lockFilters) {
      setFiltersRefreshing(true);
    }
    if (!append) {
      setError(null);
    }

    const payload = buildFilterPayload(nextFilterState);

    try {
      const response = await BackendAxios.get("/entries/browse", {
        params: {
          page: requestedPage,
          limit: PAGE_SIZE,
          filters: JSON.stringify(payload),
        },
      });

      const nextEntries = response.data?.entries || [];
      const pagination = response.data?.pagination || {};
      const nextPage = pagination.page || requestedPage;
      const nextPagesTotal = pagination.pages || 1;

      if (append) {
        setEntries((prevEntries) => {
          const existingIds = new Set(prevEntries.map((entry) => String(entry._id)));
          const appendable = nextEntries.filter(
            (entry) => !existingIds.has(String(entry._id))
          );
          return [...prevEntries, ...appendable];
        });
      } else {
        setEntries(nextEntries);
      }

      setPage(nextPage);
      setTotalPages(nextPagesTotal);
      setHasMore(nextPage < nextPagesTotal);
      setAvailableFilters(getAvailableFiltersFromResponse(response.data));
    } catch (err) {
      setError(err.response?.data?.error || "Failed to load designs");
      if (!append) {
        setEntries([]);
        setPage(1);
        setTotalPages(1);
        setHasMore(false);
        setAvailableFilters({ fields: [] });
      }
    } finally {
      if (append) {
        setLoadingMore(false);
      } else {
        setLoading(false);
      }
      if (lockFilters) {
        setFiltersRefreshing(false);
      }
    }
  };

  useEffect(() => {
    const loadInitialData = async () => {
      try {
        const response = await BackendAxios.get("/entries/browse/filters");
        const initialFilters = getAvailableFiltersFromResponse(response.data);
        setAllFilterCatalog(initialFilters);
      } catch (err) {
        setAllFilterCatalog({ fields: [] });
      }

      fetchEntries(1);
    };

    loadInitialData();
  }, []);

  const loadMoreEntries = useCallback(() => {
    if (loading || loadingMore || !hasMore) return;
    fetchEntries(page + 1, filterState, false, true);
  }, [loading, loadingMore, hasMore, page, filterState]);

  const openDetailsDialog = (entry) => {
    setSelectedEntry(entry);
    setDetailsOpen(true);
  };

  const applyFilters = async (nextState) => {
    const normalized = normalizeFilterState(nextState);
    setFilterState(normalized);
    await fetchEntries(1, normalized, true);
  };

  const clearAllFilters = async () => {
    await applyFilters({
      fields: {},
      squareFootage: {
        includeBasement: Boolean(filterState?.squareFootage?.includeBasement),
      },
    });
  };

  const setSquareFootageIncludeBasement = async (includeBasement) => {
    await applyFilters({
      ...filterState,
      squareFootage: {
        includeBasement: Boolean(includeBasement),
      },
    });
  };

  const toggleStringFieldValue = async (fieldId, value) => {
    const key = String(fieldId);
    const currentField = filterState.fields?.[key] || {
      values: [],
      min: "",
      max: "",
    };
    const currentValues = currentField.values || [];
    const nextValues = currentValues.includes(value)
      ? currentValues.filter((item) => item !== value)
      : [...currentValues, value];

    const nextFields = { ...(filterState.fields || {}) };
    const nextConfig = { ...currentField, values: nextValues };

    if (hasAnyFieldFilter(nextConfig)) {
      nextFields[key] = nextConfig;
    } else {
      delete nextFields[key];
    }

    await applyFilters({
      ...filterState,
      fields: nextFields,
    });
  };

  const onNumberFilterDraftChange = (fieldId, part, value) => {
    const key = String(fieldId);
    const currentField = filterState.fields?.[key] || {
      values: [],
      min: "",
      max: "",
    };
    const nextFields = {
      ...(filterState.fields || {}),
      [key]: {
        ...currentField,
        [part]: value,
      },
    };

    setFilterState({
      ...filterState,
      fields: nextFields,
    });
  };

  const normalizeNumberDraftForBounds = (fieldId, sliderMin, sliderMax) => {
    const key = String(fieldId);
    const currentField = filterState.fields?.[key] || {
      values: [],
      min: "",
      max: "",
    };
    let nextMin = currentField.min;
    let nextMax = currentField.max;

    const parsedMin = parseOptionalNumber(currentField.min);
    const parsedMax = parseOptionalNumber(currentField.max);

    if (parsedMin !== null) {
      nextMin = String(clampNumber(parsedMin, sliderMin, sliderMax));
    }
    if (parsedMax !== null) {
      nextMax = String(clampNumber(parsedMax, sliderMin, sliderMax));
    }

    const nextFields = {
      ...(filterState.fields || {}),
      [key]: {
        ...currentField,
        min: nextMin,
        max: nextMax,
      },
    };

    setFilterState({
      ...filterState,
      fields: nextFields,
    });
  };

  const isNumberFilterInvalid = (fieldId) => {
    const key = String(fieldId);
    const currentField = filterState.fields?.[key] || { min: "", max: "" };
    const { min, max } = currentField;

    if (min !== "" && !isNumericInput(min)) return true;
    if (max !== "" && !isNumericInput(max)) return true;

    const parsedMin = parseOptionalNumber(min);
    const parsedMax = parseOptionalNumber(max);
    if (parsedMin !== null && parsedMax !== null && parsedMin > parsedMax)
      return true;

    return false;
  };

  const commitNumberFilter = async (
    fieldId,
    sliderMin,
    sliderMax,
    fallbackMin = sliderMin,
    fallbackMax = sliderMax
  ) => {
    const key = String(fieldId);
    const currentField = filterState.fields?.[key] || {
      values: [],
      min: "",
      max: "",
    };
    const nextFields = { ...(filterState.fields || {}) };
    const minParsed = parseOptionalNumber(currentField.min);
    const maxParsed = parseOptionalNumber(currentField.max);

    let normalizedMin =
      minParsed !== null
        ? clampNumber(minParsed, sliderMin, sliderMax)
        : clampNumber(fallbackMin, sliderMin, sliderMax);
    let normalizedMax =
      maxParsed !== null
        ? clampNumber(maxParsed, sliderMin, sliderMax)
        : clampNumber(fallbackMax, sliderMin, sliderMax);
    if (
      normalizedMin !== null &&
      normalizedMax !== null &&
      normalizedMin > normalizedMax
    ) {
      normalizedMax = normalizedMin;
    }

    const nextConfig = {
      ...currentField,
      min: String(normalizedMin),
      max: String(normalizedMax),
    };

    if (hasAnyFieldFilter(nextConfig)) {
      nextFields[key] = nextConfig;
    } else {
      delete nextFields[key];
    }

    await applyFilters({
      ...filterState,
      fields: nextFields,
    });
  };

  const setNumberFieldFromSlider = (fieldId, min, max) => {
    const key = String(fieldId);
    const currentField = filterState.fields?.[key] || {
      values: [],
      min: "",
      max: "",
    };
    const nextMin = Math.min(min, max);
    const nextMax = Math.max(min, max);
    const nextFields = {
      ...(filterState.fields || {}),
      [key]: {
        ...currentField,
        min: String(nextMin),
        max: String(nextMax),
      },
    };

    setFilterState({
      ...filterState,
      fields: nextFields,
    });
  };

  const commitNumberFilterRange = async (fieldId, min, max) => {
    const key = String(fieldId);
    const currentField = filterState.fields?.[key] || {
      values: [],
      min: "",
      max: "",
    };
    const nextFields = { ...(filterState.fields || {}) };
    const nextMin = Math.min(min, max);
    const nextMax = Math.max(min, max);
    const nextConfig = {
      ...currentField,
      min: String(nextMin),
      max: String(nextMax),
    };

    if (hasAnyFieldFilter(nextConfig)) {
      nextFields[key] = nextConfig;
    } else {
      delete nextFields[key];
    }

    await applyFilters({
      ...filterState,
      fields: nextFields,
    });
  };

  const resetSingleFieldFilter = async (fieldId) => {
    const key = String(fieldId);
    const nextFields = { ...(filterState.fields || {}) };
    delete nextFields[key];

    await applyFilters({
      ...filterState,
      fields: nextFields,
    });
  };

  return (
    <Box
      className="browse-designs-page"
      sx={{
        height: {
          xs: `${
            (sizing?.vh || window.innerHeight) - (sizing?.xs?.usedHeight || 0)
          }px`,
          sm: `${
            (sizing?.vh || window.innerHeight) - (sizing?.sm?.usedHeight || 0)
          }px`,
        },
      }}
    >
      <Filters
        sizing={sizing}
        filterDrawerOpen={filterDrawerOpen}
        setFilterDrawerOpen={setFilterDrawerOpen}
        filtersRefreshing={filtersRefreshing}
        clearAllFilters={clearAllFilters}
        allFilterCatalog={allFilterCatalog}
        availableFilters={availableFilters}
        filterState={filterState}
        activeNumberSliderFieldId={activeNumberSliderFieldId}
        setActiveNumberSliderFieldId={setActiveNumberSliderFieldId}
        parseOptionalNumber={parseOptionalNumber}
        hasAnyFieldFilter={hasAnyFieldFilter}
        resetSingleFieldFilter={resetSingleFieldFilter}
        setNumberFieldFromSlider={setNumberFieldFromSlider}
        commitNumberFilterRange={commitNumberFilterRange}
        onNumberFilterDraftChange={onNumberFilterDraftChange}
        normalizeNumberDraftForBounds={normalizeNumberDraftForBounds}
        isNumberFilterInvalid={isNumberFilterInvalid}
        commitNumberFilter={commitNumberFilter}
        setSquareFootageIncludeBasement={setSquareFootageIncludeBasement}
        toggleStringFieldValue={toggleStringFieldValue}
      />

      <Cards
        entries={entries}
        loading={loading}
        loadingMore={loadingMore}
        error={error}
        hasMore={hasMore}
        onLoadMore={loadMoreEntries}
        onOpenDetails={openDetailsDialog}
      />
      <DetailsDialog
        open={detailsOpen}
        onClose={() => setDetailsOpen(false)}
        entry={selectedEntry}
        sizing={sizing}
      />
    </Box>
  );
}

export default BrowseDesigns;


